import React, { useState, useEffect, useContext } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import {
  Box,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { NextImage } from '@sdf/base';
import { useRouter } from 'next/router';
import { sideDrawerStyle as style } from '../../../styles';
import { getDashboardDrawerConfig } from '../../../utils';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { useAppContext } from '../../../hooks/context';
import { CreditContext } from '../../../context/CreditProvider';
import { Icon } from '@iconify/react';

interface Props {
  isDrawerOpen: boolean;
  onToggleDrawer: () => void;
}

const EXPANDED_MENU_KEY = 'expandedMenu';

const MenuList: React.FC<Props> = ({ isDrawerOpen, onToggleDrawer }) => {
  const { push, pathname } = useRouter();
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { userData } = useAppContext();
  const { userPlanData, agentPlanData } = useContext(CreditContext);
  const [expandedMenu, setExpandedMenu] = useState<string[]>(() => {
    const storedValue = localStorage.getItem(EXPANDED_MENU_KEY);
    try {
      const parsedValue =
        JSON.parse(storedValue).length > 0
          ? JSON.parse(storedValue)
          : ['ai-create', 'labs', 'ai-media', 'admin', 'ai-avatars', 'ai-optimize', 'dashboard'];
      return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
    } catch (error) {
      return ['ai-create', 'labs', 'ai-media', 'admin', 'ai-avatars', 'ai-optimize', 'dashboard'];
    }
  });

  const handleToggle = (id: string) => {
    if (!isDrawerOpen) {
      onToggleDrawer();
    }

    const newExpandedMenu = expandedMenu.includes(id)
      ? expandedMenu.filter((menuId) => menuId !== id)
      : [...expandedMenu, id];

    setExpandedMenu(newExpandedMenu);
    localStorage.setItem(EXPANDED_MENU_KEY, JSON.stringify(newExpandedMenu));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: 'background.secondary',
        overflow: 'auto',
        height: '100%',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <List key="SidePanel-Menu">
        <Divider
          sx={{ borderColor: theme.palette.background.sidebarBorder, borderWidth: '1px', my: 2 }}
        />

        {getDashboardDrawerConfig({
          pathname,
          isAdmin: userData?.isAdmin,
          userPlanData,
          agentPlanData,
        }).map(({ id, label, link, isActive, iconPath, subLinks, isLocked }, index) => (
          <Box key={id}>
            <ListItem
              disablePadding
              sx={{
                display: 'block',
                // paddingTop: index === 0 ? 4 : 0,
              }}
              onClick={() => {
                if (!subLinks?.length) {
                  push(link);
                  isExtraSmall && onToggleDrawer();
                }
              }}
            >
              <ListItemButton
                sx={{
                  ...style.listBtn,
                  borderColor: 'primary.main',
                  color: isActive
                    ? theme.palette.background.activeBorder
                    : theme.palette.text.primary,
                  background: isActive ? theme.palette.background.activeCard : 'transparent',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  '&:hover': {
                    bgcolor: isActive
                      ? theme.palette.background.activeCard
                      : theme.palette.background.activeCard,
                    boxShadow: isActive ? 'none' : theme.shadows[6],
                  },
                }}
                onClick={() => handleToggle(id)}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Stack direction="row" alignItems="center" columnGap={1}>
                    <NextImage
                      src={iconPath}
                      alt=""
                      height={24}
                      width={24}
                      sx={{ color: 'red', height: 'auto' }}
                    />
                    {isDrawerOpen && (
                      <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{label}</Typography>
                    )}
                  </Stack>
                  {isDrawerOpen && subLinks?.length > 0 && (
                    <Box sx={{ marginLeft: 'auto' }}>
                      {expandedMenu.includes(id) ? (
                        <KeyboardArrowUpRoundedIcon sx={{ fontSize: 30 }} />
                      ) : (
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
                      )}
                    </Box>
                  )}
                  {isLocked && (
                    <Tooltip title={'Upgrade To A Paid Account For All Feature Access'}>
                      <Icon icon="mdi:lock-outline" width={'17'} height={'17'} />
                    </Tooltip>
                  )}
                </Stack>
              </ListItemButton>
            </ListItem>
            {subLinks?.length > 0 && isDrawerOpen && (
              <Collapse
                in={expandedMenu.includes(id)}
                timeout="auto"
                unmountOnExit
                sx={{ display: 'flex', flexDirection: 'column', rowGap: 1, mt: 0.5 }}
              >
                <List component="div" disablePadding>
                  {subLinks.map((subLink, sublinkIndex) => (
                    <ListItem
                      key={subLink.id}
                      data-tut={subLink.id}
                      disablePadding
                      sx={{
                        pl: 4,
                        py: 0.2,
                        pr: 1,
                        borderRadius: 1,
                        mt: sublinkIndex > 0 ? 0.5 : 0,
                        backgroundColor: subLink.isActive
                          ? theme.palette.background.secondary
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.background.activeCard,
                        },
                      }}
                      onClick={() => {
                        push(subLink.link);
                        isExtraSmall && onToggleDrawer();
                      }}
                    >
                      <ListItemButton
                        sx={{
                          ml: -0.5,
                          color: subLink.isActive
                            ? theme.palette.background.activeBorder
                            : theme.palette.text.primary,
                          '&:hover': {
                            backgroundColor: theme.palette.background.activeCard,
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 14 }}>{subLink.label}</Typography>
                      </ListItemButton>
                      {subLink.isLocked && (
                        <Tooltip title={'Upgrade To A Paid Account For All Feature Access'}>
                          <Icon icon="mdi:lock-outline" width={'17'} height={'17'} />
                        </Tooltip>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
            {getDashboardDrawerConfig({
              pathname: '',
              isAdmin: userData?.isAdmin,
              userPlanData,
              agentPlanData,
            }).length !==
              index + 1 && (
              <Divider
                sx={{
                  borderColor: theme.palette.background.sidebarBorder,
                  borderWidth: '1px',
                  my: 2,
                }}
              />
            )}
          </Box>
        ))}
      </List>
    </Box>
  );
};

export default MenuList;
